import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    useGetSignupConfigQuery,
    useGetCookieConsentQuery,
    useSaveCookieConsentMutation,
    useLinkCookieConsentSessionMutation,
} from '../services/nestjs-base-api';

const COOKIE_CONSENT_SESSION_KEY = 'cookie_consent_session_id';
const COOKIE_CONSENT_LOCAL_KEY = 'cookie_consent_preferences';

interface CookieConsentPreferences {
    analyticsConsent: boolean;
    marketingConsent: boolean;
    consentGivenAt?: string;
}

interface UseCookieConsentOptions {
    jwtToken?: string | null;
}

interface UseCookieConsentResult {
    // Feature status
    isEnabled: boolean;
    isLoading: boolean;

    // Consent status
    hasConsent: boolean;
    analyticsConsent: boolean;
    marketingConsent: boolean;

    // Actions
    acceptAll: () => Promise<void>;
    rejectAll: () => Promise<void>;
    savePreferences: (analytics: boolean, marketing: boolean) => Promise<void>;
    linkSessionToUser: () => Promise<void>;

    // Session management
    sessionId: string;
}

/**
 * Generate a unique session ID for anonymous users
 */
function generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Get or create a session ID for cookie consent tracking
 */
function getOrCreateSessionId(): string {
    let sessionId = localStorage.getItem(COOKIE_CONSENT_SESSION_KEY);
    if (!sessionId) {
        sessionId = generateSessionId();
        localStorage.setItem(COOKIE_CONSENT_SESSION_KEY, sessionId);
    }
    return sessionId;
}

/**
 * Get locally stored consent preferences (for offline/quick access)
 */
function getLocalConsentPreferences(): CookieConsentPreferences | null {
    try {
        const stored = localStorage.getItem(COOKIE_CONSENT_LOCAL_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch {
        // Ignore parsing errors
    }
    return null;
}

/**
 * Save consent preferences locally
 */
function saveLocalConsentPreferences(preferences: CookieConsentPreferences): void {
    try {
        localStorage.setItem(COOKIE_CONSENT_LOCAL_KEY, JSON.stringify(preferences));
    } catch {
        // Ignore storage errors
    }
}

/**
 * Hook to manage cookie consent
 *
 * Usage:
 * ```tsx
 * const {
 *     isEnabled,
 *     hasConsent,
 *     analyticsConsent,
 *     acceptAll,
 *     rejectAll,
 *     savePreferences,
 * } = useCookieConsent({ jwtToken: user?.jwtToken });
 *
 * // Show banner if feature is enabled but no consent given yet
 * {isEnabled && !hasConsent && (
 *     <CookieConsentBanner
 *         onAcceptAll={acceptAll}
 *         onRejectAll={rejectAll}
 *         onSavePreferences={savePreferences}
 *     />
 * )}
 * ```
 */
export function useCookieConsent({
    jwtToken,
}: UseCookieConsentOptions = {}): UseCookieConsentResult {
    const sessionId = useMemo(() => getOrCreateSessionId(), []);

    // Check if cookie consent feature is enabled
    const { data: config, isLoading: configLoading } = useGetSignupConfigQuery();

    // Get current consent status from API
    const { data: consentData, isLoading: consentLoading } = useGetCookieConsentQuery(
        { sessionId, jwtToken: jwtToken || undefined },
        { skip: !config?.cookieConsentEnabled }
    );

    // Mutations
    const [saveConsent] = useSaveCookieConsentMutation();
    const [linkSession] = useLinkCookieConsentSessionMutation();

    // Local state for immediate UI updates
    const [localPreferences, setLocalPreferences] = useState<CookieConsentPreferences | null>(
        () => getLocalConsentPreferences()
    );

    // Sync local preferences when API data changes
    useEffect(() => {
        if (consentData?.hasConsent) {
            const prefs: CookieConsentPreferences = {
                analyticsConsent: consentData.analyticsConsent,
                marketingConsent: consentData.marketingConsent,
                consentGivenAt: consentData.consentGivenAt,
            };
            setLocalPreferences(prefs);
            saveLocalConsentPreferences(prefs);
        }
    }, [consentData]);

    // Determine effective consent status
    const hasConsent = localPreferences !== null || consentData?.hasConsent || false;
    const analyticsConsent = localPreferences?.analyticsConsent ?? consentData?.analyticsConsent ?? false;
    const marketingConsent = localPreferences?.marketingConsent ?? consentData?.marketingConsent ?? false;

    const savePreferences = useCallback(async (analytics: boolean, marketing: boolean) => {
        // Update local state immediately for responsive UI
        const prefs: CookieConsentPreferences = {
            analyticsConsent: analytics,
            marketingConsent: marketing,
            consentGivenAt: new Date().toISOString(),
        };
        setLocalPreferences(prefs);
        saveLocalConsentPreferences(prefs);

        // Save to API
        try {
            await saveConsent({
                analyticsConsent: analytics,
                marketingConsent: marketing,
                sessionId,
                jwtToken: jwtToken || undefined,
            }).unwrap();
        } catch (error) {
            console.error('Failed to save cookie consent:', error);
            // Local storage will serve as fallback
        }
    }, [saveConsent, sessionId, jwtToken]);

    const acceptAll = useCallback(async () => {
        await savePreferences(true, true);
    }, [savePreferences]);

    const rejectAll = useCallback(async () => {
        await savePreferences(false, false);
    }, [savePreferences]);

    const linkSessionToUser = useCallback(async () => {
        if (!jwtToken) return;

        try {
            await linkSession({ sessionId, jwtToken }).unwrap();
        } catch (error) {
            console.error('Failed to link cookie consent session:', error);
        }
    }, [linkSession, sessionId, jwtToken]);

    return {
        isEnabled: config?.cookieConsentEnabled ?? false,
        isLoading: configLoading || consentLoading,
        hasConsent,
        analyticsConsent,
        marketingConsent,
        acceptAll,
        rejectAll,
        savePreferences,
        linkSessionToUser,
        sessionId,
    };
}

export default useCookieConsent;
